import React, { useState, useEffect } from 'react';
import {
  Button,
  RadioGroup,
  Radio,
  CircularProgress,
  Box,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  getDepartureWindowOptions,
  getSchoolDepartureWindowOptions
} from '../utils/departureWindowOptions';
import { Bus, RouteType, RouteWithDetailedView } from '../types';
import { User } from '../../../types/userType';
import DialogModal from '../../../components/DialogModal';
import { SnackbarSeverityType } from '../../../enums';
import { upsertRoute } from '../../../apis/transport/routes';
import DropdownSelect, {
  DropdownSelectOption
} from '../../../components/DropdownSelect/DropdownSelect';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import { StyledRadioButton } from '../../common/styled';
import pallet from '../../../layouts/pallet';

interface EditRouteDialogProps {
  open: boolean;
  onClose: () => void;
  onEditRoute: () => void;
  loggedInUser: User;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  route: RouteWithDetailedView | null;
  buses: Bus[];
}

const EditRouteDialog: React.FC<EditRouteDialogProps> = ({
  open,
  onClose,
  onEditRoute,
  loggedInUser,
  showToast,
  route,
  buses
}) => {
  const [name, setName] = useState('');
  const [departureWindow, setDepartureWindow] = useState('');
  const [busId, setBusId] = useState<string | null>(null);
  const [type, setType] = useState<RouteType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [departureWindowOptions, setDepartureWindowOptions] = useState<
    string[]
  >(getDepartureWindowOptions());
  const [busOptions, setBusOptions] = useState<DropdownSelectOption[]>([]);

  useEffect(() => {
    if (open && route) {
      setName(route.name);
      setDepartureWindow(
        `${route.departureWindow.from} and ${route.departureWindow.to}`
      );
      setBusId(route.busId || null);
      setType(route.type);
      setIsLoading(false);
    }
  }, [open, route]);

  useEffect(() => {
    if (open && type && loggedInUser?.school) {
      getSchoolDepartureWindowOptions(loggedInUser.school, type).then(
        setDepartureWindowOptions
      );
    }
  }, [open, type, loggedInUser?.school]);

  useEffect(() => {
    if (buses) {
      setBusOptions(buses.map((bus) => ({ value: bus.id, label: bus.name })));
    }
  }, [buses]);

  const handleEditRoute = async () => {
    if (!type || !route) return;
    setIsLoading(true);
    try {
      const [from, to] = departureWindow.split(' and ');
      await upsertRoute(loggedInUser.school, {
        id: route.id,
        name,
        departureWindow: { from: from.trim(), to: to.trim() },
        busId: busId || undefined,
        type,
        isDeleted: false
      });
      onEditRoute();
      onClose();
      showToast('Route updated successfully!', SnackbarSeverityType.success);
    } catch (error) {
      showToast('Failed to update route.', SnackbarSeverityType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const isEditButtonDisabled = !name || !departureWindow || !type || isLoading;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Update Route Details'
      actions={
        <>
          <Button
            onClick={onClose}
            sx={{ border: `1px solid ${pallet.primary}` }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditRoute}
            disabled={isEditButtonDisabled}
            variant='contained'
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Update'
            )}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <TextInputWithIcon
          label='Route ID'
          placeholder='Route ID'
          value={route?.id || ''}
          disabled
          fullWidth
        />
        <FormControl>
          <FormLabel
            sx={{
              mb: 0.5,
              color: 'text.primary',
              fontWeight: 600,
              '& .MuiFormLabel-asterisk': { color: 'error.main' }
            }}
            required
          >
            Route Type
          </FormLabel>
          <RadioGroup
            row
            value={type}
            onChange={(e) => setType(e.target.value as RouteType)}
            sx={{ gap: 2 }}
          >
            <StyledRadioButton
              value={RouteType.PICK_UP_ROUTE}
              control={<Radio />}
              label='Pick up Route'
            />
            <StyledRadioButton
              value={RouteType.DROP_OFF_ROUTE}
              control={<Radio />}
              label='Drop off Route'
            />
          </RadioGroup>
        </FormControl>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <TextInputWithIcon
            label='Route Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Enter a Route Name'
            required
            fullWidth
            sx={{ flex: 1 }}
          />
          <DropdownSelect
            label='Departure Window'
            placeholder='Select Departure Window'
            name='departureWindow'
            value={departureWindow}
            options={departureWindowOptions.map((option) => ({
              value: option,
              label: option
            }))}
            onChange={(e) => setDepartureWindow(e.target.value as string)}
            required
            fullWidth
            sx={{ flex: 1 }}
          />
        </Box>
        <DropdownSelect
          label='Assign Bus'
          placeholder='Select Bus'
          name='busId'
          value={busId || ''}
          options={busOptions}
          onChange={(e) => setBusId(e.target.value as string)}
          fullWidth
        />
      </Box>
    </DialogModal>
  );
};

export default EditRouteDialog;
